import React, { useCallback, useContext } from 'react';
import classnames from 'classnames';
import { useFileUpload, FileUploadOptions } from '@wix/da-shared-react/pkg/utils/hooks/useFileUpload';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import ImageIcon from '@wix/da-ds/pkg/Icons/24x24/Image';
import AddVideoIcon from '@wix/da-ds/pkg/Icons/24x24/AddVideo';
import GifIcon from '@wix/da-ds/pkg/Icons/24x24/AddGif';
import { IMAGE_MIME, VIDEO_MIME } from '@wix/da-shared-react/pkg/utils/mime';
import { UploadSuccessCallback } from '@wix/da-shared-react/pkg/redux/uploads/types';
import s from './FileTypeButton.scss';

export type FileType = 'images' | 'videos' | 'gifs';

export interface Props {
  fileType: FileType;
  uploadId: string;
  onSuccess: UploadSuccessCallback;
  uploadOptions?: FileUploadOptions;
}

const FILE_TYPE_CONFIG: Record<
  FileType,
  { icon: React.FC<any>; label: string; mimeTypes: string[] }
> = {
  images: {
    icon: ImageIcon,
    label: 'Images',
    mimeTypes: IMAGE_MIME.filter(mime => mime !== 'image/gif'),
  },
  videos: {
    icon: AddVideoIcon,
    label: 'Videos',
    mimeTypes: VIDEO_MIME,
  },
  gifs: {
    icon: GifIcon,
    label: 'Gifs',
    mimeTypes: ['image/gif'],
  },
};

const FileTypeButton: React.FC<Props> = ({
  fileType,
  uploadId,
  onSuccess,
  uploadOptions,
}) => {
  const isMobile = useContext(MobileContext);
  const config = FILE_TYPE_CONFIG[fileType];
  const Icon = config.icon;

  const { input, showFilePicker } = useFileUpload({
    id: `${uploadId}-${fileType}`,
    acceptedMimeTypes: config.mimeTypes,
    allowMultiple: true,
    allowDragAndDrop: false,
    allowClickOnRoot: false,
    isDraft: true,
    useDefaults: true,
    onSuccess,
    ...uploadOptions,
  });

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      showFilePicker();
    },
    [showFilePicker]
  );

  return (
    <button
      type="button"
      className={classnames(s['root'], isMobile && s['mobile'])}
      onClick={handleClick}
    >
      <div className={s['icon']}>
        <Icon size={IconSize.MEDIUM} disableDefaultColors />
      </div>
      <div className={s['label']}>{config.label}</div>
      {input}
    </button>
  );
};
FileTypeButton.displayName = 'FileTypeButton';

export default FileTypeButton;
